import {
    useGetRelatedScenarioResultsLazyQuery,
    useGetRemainingScenarioResultsLazyQuery,
    useGetScenarioResultsByProjectIdLazyQuery
} from "generated/graphql";
import {
    convertHasuraScenarioDataToScenarioResults,
    convertHasuraScenarioDataToScenarioResultsWithExistingData,
    ScenarioAllocatedUpgrade,
    ScenarioConstraint,
    ScenarioResults
} from "in_queue/types/scenarioResultType";
import {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useState
} from "react";
import { Loading } from "types/loadingType";
import { useRegionConfig } from "./ClusterContext";
import { useSubscriptionForCluster } from "./ProjectDataContext";
import {
    useCurrentScenarioMetadata,
    useCurrentTrackedProject
} from "./ScenarioPageContext";

interface ScenarioResultsContextValue {
    scenarioResults: Loading<ScenarioResults>;
    /**
     * `true` while the SECOND fetch batch (remaining / related project
     * results) is still loading on top of the initial current-project
     * fetch. Charts that depend on cross-project data (e.g. Top Upgrades)
     * wait on this flipping `false` before rendering to avoid flicker.
     *
     * Resets to `true` whenever `scenarioId` changes.
     */
    isLoadingRemaining: boolean;
}

export const ScenarioResultsContext =
    createContext<ScenarioResultsContextValue>({
        scenarioResults: "loading",
        isLoadingRemaining: true
    });

export const ScenarioResultsProvider: React.FC<PropsWithChildren> = ({
    children
}) => {
    const project = useCurrentTrackedProject();
    const { scenarioId } = useCurrentScenarioMetadata();
    const regionConfig = useRegionConfig();
    const subscription = useSubscriptionForCluster();
    const hasUnlimitedProjects =
        subscription !== "loading" && subscription?.projectLimit === null;
    const [scenarioResults, setScenarioResults] =
        useState<Loading<ScenarioResults>>("loading");
    const [isLoadingRemaining, setIsLoadingRemaining] = useState(true);

    const [getScenarioResultsByProjectId] =
        useGetScenarioResultsByProjectIdLazyQuery();
    const [getRemainingScenarioResults] =
        useGetRemainingScenarioResultsLazyQuery();
    const [getRelatedScenarioResults] = useGetRelatedScenarioResultsLazyQuery();

    useEffect(() => {
        setIsLoadingRemaining(true);
        // Two main points of consideration:
        // We need to display different scenario results depending on the subscription's projectLimit
        // We want to start showing data ASAP
        const fetchData = async () => {
            let scenarioResults: Loading<ScenarioResults> = "loading";

            const { data, loading } = await getScenarioResultsByProjectId({
                variables: {
                    scenarioId,
                    projectId: project.projectId
                }
            });

            if (data && !loading) {
                scenarioResults = convertHasuraScenarioDataToScenarioResults(
                    data,
                    project,
                    regionConfig
                );

                // Set the initial data immediately to start rendering
                setScenarioResults(scenarioResults);

                if (hasUnlimitedProjects) {
                    // If the subscription has unlimited projects, we can show all other scenario results
                    const {
                        data: remainingScenarioResultsData,
                        loading: remainingScenarioResultsLoading
                    } = await getRemainingScenarioResults({
                        variables: {
                            scenarioId,
                            projectId: project.projectId
                        }
                    });

                    if (
                        remainingScenarioResultsData &&
                        !remainingScenarioResultsLoading
                    ) {
                        const allScenarioResults =
                            convertHasuraScenarioDataToScenarioResultsWithExistingData(
                                {
                                    hasuraResult: remainingScenarioResultsData,
                                    existingScenarioResults: scenarioResults,
                                    currentProject: project,
                                    regionConfig
                                }
                            );

                        setScenarioResults(allScenarioResults);
                    }
                } else {
                    // If the subscription has a project limit, we only show scenario results for the current
                    // tracked project, and any other projects that are related to it (i.e. projects that share
                    // network upgrades or overload the same facilities). This is to prevent users from seeing
                    // results for projects they don't have access to
                    const networkUpgradeNames =
                        scenarioResults.allocatedUpgrades.map(
                            (upgrade: ScenarioAllocatedUpgrade) =>
                                upgrade.networkUpgradeName
                        );
                    const monitoredFacilityNames =
                        scenarioResults.constraints.map(
                            (constraint: ScenarioConstraint) =>
                                constraint.monitoredFacilityName
                        );

                    const {
                        data: relatedScenarioResultsData,
                        loading: relatedScenarioResultsLoading
                    } = await getRelatedScenarioResults({
                        variables: {
                            scenarioId,
                            projectId: project.projectId,
                            networkUpgradeNames,
                            monitoredFacilityNames
                        }
                    });

                    if (
                        relatedScenarioResultsData &&
                        !relatedScenarioResultsLoading
                    ) {
                        const allScenarioResults =
                            convertHasuraScenarioDataToScenarioResultsWithExistingData(
                                {
                                    hasuraResult: relatedScenarioResultsData,
                                    existingScenarioResults: scenarioResults,
                                    currentProject: project,
                                    regionConfig
                                }
                            );

                        setScenarioResults(allScenarioResults);
                    }
                }
            }
            setIsLoadingRemaining(false);
        };

        fetchData();
    }, [scenarioId, hasUnlimitedProjects]);
    return (
        <ScenarioResultsContext.Provider
            value={{ scenarioResults, isLoadingRemaining }}
        >
            {children}
        </ScenarioResultsContext.Provider>
    );
};

export const useScenarioResults = (): ScenarioResultsContextValue => {
    return useContext(ScenarioResultsContext);
};
